import { fetchJSON as fetchAPI } from './client';

export type GovernanceDashboard = {
  tenant_id: string;
  active_policies: number;
  policy_violations_24h: number;
  audit_events_24h: number;
  open_risks: number;
  security_events_24h: number;
  pending_privacy_requests: number;
  compliance_controls: number;
  trust_score: number;
  unread_notifications: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type GovernancePolicy = {
  id: string;
  category: string;
  name: string;
  description?: string;
  status: string;
  version: number;
  testable: boolean;
  rules?: Record<string, unknown>;
  created_at: string;
};

export type AuditLogEntry = {
  id: string;
  domain: string;
  action: string;
  actor_id?: string;
  resource_id?: string;
  detail?: string;
  immutable: boolean;
  created_at: string;
};

export type ComplianceControl = {
  id: string;
  framework: string;
  control_id: string;
  name: string;
  description?: string;
  status: string;
  evidence?: string[];
};

export type PrivacyRequest = {
  id: string;
  type: string;
  status: string;
  subject_id?: string;
  classification?: string;
  detail?: string;
  created_at: string;
};

export type RiskAssessment = {
  id: string;
  category: string;
  title: string;
  description?: string;
  level: string;
  score: number;
  mitigation?: string;
  auto_detected: boolean;
};

export type RetentionRule = {
  id: string;
  name: string;
  data_type: string;
  classification: string;
  retention_days: number;
  auto_delete: boolean;
  active: boolean;
};

export type TrustReport = {
  tenant_id: string;
  security_score: number;
  compliance_score: number;
  ai_governance_score: number;
  marketplace_score: number;
  overall_trust_score: number;
  active_policies: number;
  open_risks: number;
  pending_privacy_requests: number;
  audit_events_24h: number;
  generated_at: string;
};

export type PolicyEvaluationResult = {
  allowed: boolean;
  policy_id?: string;
  policy_name?: string;
  requires_approval: boolean;
  messages?: string[];
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const governanceApi = {
  dashboard: () => fetchJSON<GovernanceDashboard>('/dashboard'),
  domains: () => fetchJSON<string[]>('/domains'),

  listPolicies: (category?: string) =>
    fetchJSON<GovernancePolicy[]>(`/policies${category ? `?category=${category}` : ''}`),

  createPolicy: (body: { category: string; name: string; description?: string; rules?: Record<string, unknown> }) =>
    fetchJSON<GovernancePolicy>('/policies', { method: 'POST', body: JSON.stringify(body) }),

  evaluatePolicy: (body: { category: string; action: string; resource_id?: string }) =>
    fetchJSON<PolicyEvaluationResult>('/policies/evaluate', { method: 'POST', body: JSON.stringify(body) }),

  listAudits: (domain?: string, limit = 50) =>
    fetchJSON<AuditLogEntry[]>(`/audit?limit=${limit}${domain ? `&domain=${domain}` : ''}`),

  searchAudits: (query: string) =>
    fetchJSON<AuditLogEntry[]>('/audit/search', { method: 'POST', body: JSON.stringify({ query }) }),

  recordAudit: (body: { domain: string; action: string; detail?: string; actor_id?: string }) =>
    fetchJSON<AuditLogEntry>('/audit', { method: 'POST', body: JSON.stringify(body) }),

  listCompliance: (framework?: string) =>
    fetchJSON<ComplianceControl[]>(`/compliance/controls${framework ? `?framework=${framework}` : ''}`),

  listPrivacyRequests: () => fetchJSON<PrivacyRequest[]>('/privacy/requests'),

  submitPrivacyRequest: (body: { type: string; subject_id?: string; detail?: string }) =>
    fetchJSON<PrivacyRequest>('/privacy/requests', { method: 'POST', body: JSON.stringify(body) }),

  listRisks: () => fetchJSON<RiskAssessment[]>('/risks'),
  listRetentionRules: () => fetchJSON<RetentionRule[]>('/retention/rules'),
  trustCenter: () => fetchJSON<TrustReport>('/trust'),
  listActivity: (limit = 15) => fetchJSON<ActivityEntry[]>(`/activity?limit=${limit}`),
  adminHealth: () => fetchJSON<Record<string, unknown>>('/admin/health'),
};
